import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'safeHtml',
  standalone: true
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(html: string | null | undefined): SafeHtml {
    if (!html) {
      return '';
    }
    
    let processedHtml = html;
    
    // Check if it's already HTML
    const hasHtmlTags = /<(p|div|ul|ol|li|br|strong|em|b|i|h[1-6])[^>]*>/i.test(html);
    
    if (!hasHtmlTags) {
      // Plain text - need to format it
      let text = html.trim();
      
      // Check if text contains asterisks (bullet points)
      if (text.includes('*')) {
        // Split into sections before and after "Upper floor:" or similar section markers
        const sections: string[] = [];
        const sectionRegex = /([^:]+:)/g;
        let lastIndex = 0;
        let match;
        
        while ((match = sectionRegex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            sections.push(text.substring(lastIndex, match.index));
          }
          lastIndex = match.index;
        }
        if (lastIndex < text.length) {
          sections.push(text.substring(lastIndex));
        }
        
        if (sections.length === 0) {
          sections.push(text);
        }
        
        const htmlParts: string[] = [];
        
        for (const section of sections) {
          const trimmedSection = section.trim();
          if (!trimmedSection) continue;
          
          // Check if this section has asterisks
          if (trimmedSection.includes('*')) {
            // Split by asterisks
            const parts = trimmedSection.split('*').map(p => p.trim()).filter(p => p);
            
            for (let i = 0; i < parts.length; i++) {
              const part = parts[i];
              
              // Check if this looks like a section header (ends with colon)
              if (part.endsWith(':')) {
                htmlParts.push(`<p class="font-semibold mt-4 mb-2">${part}</p>`);
              } else if (i > 0 || trimmedSection.startsWith('*')) {
                // This is a bullet point (asterisk was before it)
                if (i === 1 || (i === 0 && trimmedSection.startsWith('*'))) {
                  htmlParts.push('<ul class="list-disc pl-5 space-y-2 mb-4">');
                }
                htmlParts.push(`<li>${part}</li>`);
                
                // Close ul if this is the last item
                if (i === parts.length - 1) {
                  htmlParts.push('</ul>');
                }
              } else {
                // Regular text before any asterisks
                htmlParts.push(`<p class="mb-3">${part}</p>`);
              }
            }
          } else {
            // No asterisks in this section, treat as paragraph
            htmlParts.push(`<p class="mb-3">${trimmedSection}</p>`);
          }
        }
        
        processedHtml = htmlParts.join('');
      } else {
        // No asterisks - just split by newlines or double spaces
        const lines = text.split(/\n|(?<=\w)\s{2,}(?=[A-Z])/).map(l => l.trim()).filter(l => l);
        processedHtml = lines.map(line => `<p class="mb-3">${line}</p>`).join('');
      }
    }
    
    return this.sanitizer.bypassSecurityTrustHtml(processedHtml);
  }
}
